/**
 * Presentational picker primitives shared by all task-fields editors
 * (and TaskDetail's own field pickers). Moved verbatim out of
 * TaskDetail.tsx so this module is self-contained.
 */
import { useMemo, useState } from 'react';
import styles from './pickers.module.css';

export interface ListPickerOption {
  id: string;
  label: string;
  sub?: string | null;
}

export function PickerList({
  options,
  activeId,
  onPick,
}: {
  options: ListPickerOption[];
  activeId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className={styles.pickerList}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          className={`${styles.pickerOption} ${
            activeId === opt.id ? styles.pickerOptionActive : ''
          }`}
          onClick={() => onPick(opt.id)}
        >
          {opt.label}
          {opt.sub && <span className={styles.pickerSub}>{opt.sub}</span>}
        </button>
      ))}
    </div>
  );
}

export function SearchablePickerList({
  options,
  activeId,
  allowClear,
  onPick,
}: {
  options: ListPickerOption[];
  activeId: string | null;
  allowClear?: boolean;
  onPick: (id: string | null) => void;
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(ql) ||
        (o.sub ?? '').toLowerCase().includes(ql),
    );
  }, [q, options]);

  return (
    <div className={styles.pickerSearchable}>
      <input
        className={styles.pickerSearchInput}
        autoFocus
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className={styles.pickerList}>
        {allowClear && (
          <button
            type="button"
            className={`${styles.pickerOption} ${activeId === null ? styles.pickerOptionActive : ''}`}
            onClick={() => onPick(null)}
          >
            <span className={styles.muted}>None</span>
          </button>
        )}
        {filtered.length === 0 ? (
          <div className={styles.pickerEmpty}>No matches.</div>
        ) : (
          filtered.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`${styles.pickerOption} ${activeId === opt.id ? styles.pickerOptionActive : ''}`}
              onClick={() => onPick(opt.id)}
            >
              {opt.label}
              {opt.sub && <span className={styles.pickerSub}>{opt.sub}</span>}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
